import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { CustomTabs } from '@/components/ui/custom-tabs'
import { Calendar, MapPin, Users, Plus, Search, Clock, ArrowRight, Share2, Loader2 } from 'lucide-react'
import { formatTime } from '@/lib/utils'
import { useOpenGraph } from '@/hooks/useOpenGraph'
import { useEventos } from '@/hooks/use-eventos'
import { EVENTO_STATUS_LABEL, EVENTO_STATUS_BADGE, type EventoStatusKey } from '@/pages/evento-detalhe/constants'

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

export function EventosPage() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState<'ativo' | 'encerrado'>('ativo')

  // Buscar eventos do banco de dados
  const { data: eventos = [], isLoading, error } = useEventos({
    status: activeTab,
    busca: searchTerm,
  })

  // Configurar meta tags para compartilhamento social
  useOpenGraph({
    title: 'Eventos',
    description: 'Confira todos os eventos da nossa igreja e participe!',
    type: 'website',
  })

  // Contar eventos ativos e encerrados
  const { data: eventosAtivos = [] } = useEventos({ status: 'ativo' })
  const { data: eventosEncerrados = [] } = useEventos({ status: 'encerrado' })

  const eventTabs = [
    { id: 'ativo', label: 'Ativos', count: eventosAtivos.length },
    { id: 'encerrado', label: 'Encerrados', count: eventosEncerrados.length },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">Eventos</h1>
          <p className="text-muted-foreground">Gerencie e participe dos eventos da igreja</p>
        </div>
        <Button
          size="lg"
          className="shadow-md shadow-primary/20"
          onClick={() => navigate('/eventos/novo')}
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Evento
        </Button>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="relative"
      >
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar eventos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </motion.div>

      {/* Tabs */}
      <CustomTabs 
        tabs={eventTabs} 
        activeTab={activeTab} 
        onTabClick={(tabId) => setActiveTab(tabId as 'ativo' | 'encerrado')} 
      />

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Carregando eventos...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
          <p className="text-destructive">Erro ao carregar eventos. Tente novamente mais tarde.</p>
        </div>
      )}

      {/* Grid de Eventos */}
      {!isLoading && !error && (
        <motion.div
          key={activeTab}
          variants={container}
          initial="hidden"
          animate="show"
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {eventos.map((evento) => (
          <motion.div key={evento.id} variants={item}>
            <Card className="group h-full overflow-hidden transition-all hover:shadow-lg hover:shadow-primary/10 dark:hover:shadow-primary/5">
              {evento.capa_url && (
                <div className="relative aspect-video overflow-hidden bg-muted">
                  <img
                    src={evento.capa_url}
                    alt={evento.titulo}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  {evento.status && (
                    <Badge
                      className={`absolute right-3 top-3 ${EVENTO_STATUS_BADGE[evento.status as EventoStatusKey]}`}
                    >
                      {EVENTO_STATUS_LABEL[evento.status as EventoStatusKey]}
                    </Badge>
                  )}
                </div>
              )}
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <CardTitle className="line-clamp-2 group-hover:text-primary transition-colors">
                    {evento.titulo}
                  </CardTitle>
                  {!evento.capa_url && evento.status && (
                    <Badge className={EVENTO_STATUS_BADGE[evento.status as EventoStatusKey]}>
                      {EVENTO_STATUS_LABEL[evento.status as EventoStatusKey]}
                    </Badge>
                  )}
                </div>
                <CardDescription className="line-clamp-2">{evento.descricao}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-primary" />
                    <span className="font-medium">
                      {new Date(evento.inicio_em).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>{formatTime(evento.inicio_em)}</span>
                  </div>
                  {evento.local && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span className="line-clamp-1">{evento.local}</span>
                    </div>
                  )}
                  {evento.capacidade && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="h-4 w-4" />
                      <span>
                        {evento._count
                          ? `${evento.capacidade - evento._count.participantes} vagas disponíveis`
                          : `${evento.capacidade} vagas`}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm font-medium">
                    {evento.gratuito || evento.valor === 0 ? (
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200">
                        Gratuito
                      </Badge>
                    ) : (
                      <span className="text-primary">
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        }).format(evento.valor)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/eventos/${evento.slug}`)}
                  >
                    Ver Detalhes
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="px-3"
                    onClick={() => {
                      const inscricaoUrl = `${window.location.origin}/eventos/${evento.slug}/inscricao`
                      navigator.clipboard.writeText(inscricaoUrl)
                      alert('Link de inscrição copiado!')
                    }}
                    title="Compartilhar link de inscrição"
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          ))}
        </motion.div>
      )}

      {!isLoading && !error && eventos.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-lg border bg-card p-12 text-center"
        >
          <Calendar className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="mb-2 text-lg font-semibold text-foreground">Nenhum evento encontrado</h3>
          <p className="text-sm text-muted-foreground">
            Tente ajustar sua pesquisa ou crie um novo evento para a aba '
            {activeTab === 'ativo' ? 'Ativos' : 'Encerrados'}'
          </p>
        </motion.div>
      )}
    </div>
  )
}
